import type { ThirdPartyFetcher } from '../fetchers';

/** Host prefix for GitHub-hosted git repositories. */
export const GITHUB_PREFIX = 'github.com/';

/**
 * Per-fetcher typeahead capabilities — single source of truth for "which fetchers does the
 * Add Dependency drawer offer typeahead for, and what URL-host shapes do we recognize?".
 *
 * Co-edit with `CoreRegistrySearcher.defaultImpls`: a fetcher belongs in this table iff a
 * `RegistryServer` impl is registered for it server-side. The frontend dropdown gating and
 * the per-fetcher input-shape parsing both derive from this table, so the list stays in sync
 * across the boundary.
 *
 * `hostPrefixes`:
 * - Empty array → fetcher has no host concept (npm, mvn, hex, …); any input is a valid query.
 * - Non-empty → only inputs starting with one of these prefixes are autocompletable. Used by
 *   multi-host fetchers like `git`, where the same fetcher slot routes to different impls
 *   based on URL host (github.com/ → GitHubRegistryServer, gitlab.com/ → future GitLab impl).
 *
 * Populated as `RegistryServer` impls land.
 */
export const TYPEAHEAD_CAPABILITIES: Partial<Record<ThirdPartyFetcher, { hostPrefixes: readonly string[] }>> = {
  git: { hostPrefixes: [GITHUB_PREFIX] },
  npm: { hostPrefixes: [] },
  nuget: { hostPrefixes: [] },
};

/** Fetchers with typeahead support, derived from {@link TYPEAHEAD_CAPABILITIES}. */
export const TYPEAHEAD_FETCHERS = Object.keys(TYPEAHEAD_CAPABILITIES) as ThirdPartyFetcher[];

/**
 * Returns the substring of `input` to forward as the typeahead `q` parameter for `fetcher`,
 * or null if the input shape isn't autocompletable for this fetcher.
 *
 * For host-prefixed fetchers, the matching host prefix is stripped so length thresholds
 * (≥ N chars) count the user-entered tail, not the constant host marker. For prefixless
 * fetchers (empty `hostPrefixes`), the input is the query directly. For unsupported
 * fetchers (absent from the table), null — caller should fall back to manual entry.
 */
export function getTypeaheadQuery(fetcher: ThirdPartyFetcher, input: string): string | null {
  const cap = TYPEAHEAD_CAPABILITIES[fetcher];
  if (!cap) {
    return null;
  }
  if (cap.hostPrefixes.length === 0) {
    return input;
  }
  for (const prefix of cap.hostPrefixes) {
    if (input.startsWith(prefix)) {
      return input.slice(prefix.length);
    }
  }
  return null;
}

/**
 * Convenience predicate derived from {@link getTypeaheadQuery}: does this input match a shape
 * we autocomplete for `fetcher`? False means "manual entry" — the field still accepts the
 * value, but no suggestions are fetched and the dropdown stays closed.
 */
export function isTypeaheadable(fetcher: ThirdPartyFetcher, input: string): boolean {
  return getTypeaheadQuery(fetcher, input) !== null;
}
